import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { isAxiosError } from 'axios'

import { EducationHostedFormContextType, FormState } from './EducationHostedFormContext.types'
import { EducationHostedFormContext } from './EducationHostedFormContext'
import { formSteps } from '@percent/education-hosted-form/app/EducationHostedForm/EducationHostedForm/EducationHostedForm'
import { useQuery } from '@percent/education-hosted-form/hooks/useQuery/useQuery'
import { getPartnerConfigAction } from '@percent/education-hosted-form/api/partnerConfig/partnerConfig.actions'
import { sessionStorageIsAvailable, storage } from '@percent/education-hosted-form/utils/storage'
import { TokenError } from '@percent/education-hosted-form/errors/TokenError'
import { AppRoute } from '@percent/education-hosted-form/routing/AppRoute.enum'
import { ValidationInviteStatus } from '@percent/education-hosted-form/api/partnerConfig/partnerConfig.types'
import { APIErrorResponse } from '@percent/education-hosted-form/types/api.types'
import { EducationHostedFormSteps } from '@percent/education-hosted-form/app/EducationHostedForm/EducationHostedForm/EducationHostedForm.types'

// Result screens — not "steps", flow is done (100%) on any of them.
// Keep in sync with EducationHostedFormSteps: any new terminal enum value must
// be added here or progress will silently miscount it as an input step.
const TERMINAL_STEPS: ReadonlySet<EducationHostedFormSteps> = new Set([
  EducationHostedFormSteps.APPROVED,
  EducationHostedFormSteps.PENDING_APPROVE,
  EducationHostedFormSteps.REJECTED,
  EducationHostedFormSteps.OTP_MAX_ATTEMPTS_EXCEEDED
])

const inputSteps = (Object.keys(formSteps) as EducationHostedFormSteps[]).filter(step => !TERMINAL_STEPS.has(step))

export function EducationHostedFormContextController({ children }: PropsWithChildren) {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const pageId = searchParams.get('pageId')
  const navigate = useNavigate()

  const [formState, setFormState] = useState<FormState>(FormState.INITIAL)
  const [otpCooldownEndsAt, setOtpCooldownEndsAt] = useState<number | null>(null)
  const setOtpCooldown = useCallback((seconds: number) => setOtpCooldownEndsAt(Date.now() + seconds * 1000), [])
  const clearOtpCooldown = useCallback(() => setOtpCooldownEndsAt(null), [])
  const [mustReverifyOtp, setMustReverifyOtp] = useState(false)
  const [mustReverifyCaptcha, setMustReverifyCaptcha] = useState(false)
  const [agentVerificationId, setAgentVerificationId] = useState<string | undefined>(undefined)

  const programId = location.pathname.split('/')?.[2]

  const {
    data: partnerConfig,
    isPending: isLoading,
    isError,
    error: queryError
  } = useQuery(['partnerConfig', { programId }], getPartnerConfigAction, {
    enabled: !!programId && sessionStorageIsAvailable && !!storage.getItem('jwtToken'),
    retry: false
  })

  useEffect(() => {
    if (!isError) return

    if (
      queryError instanceof Error &&
      isAxiosError<APIErrorResponse>(queryError.cause) &&
      queryError.cause.response?.data?.error?.code === 'validation_invite/validation_invite_expired'
    ) {
      navigate(AppRoute.EXPIRED)

      return
    }

    navigate(AppRoute.NOT_FOUND)
  }, [isError, queryError, navigate])

  useEffect(() => {
    if (partnerConfig?.data?.status === ValidationInviteStatus.COMPLETE) {
      navigate(AppRoute.LINK_USED)
    }
  }, [partnerConfig?.data?.status, navigate])

  useEffect(() => {
    // eslint-disable-next-line consistent-return, prettier/prettier
    (async () => {
      try {
        const jwtToken = searchParams.get('token')

        if (!jwtToken) {
          throw new TokenError({ message: 'No token given' })
        }

        if (jwtToken && sessionStorageIsAvailable) {
          storage.setItem('jwtToken', jwtToken)
        }
      } catch (error) {
        if (error instanceof TokenError) {
          return console.warn(error)
        }
        throw error
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Being ON step N shows N-1 completed — current step ticks to 100% only after submit.
  const progress = useMemo(() => {
    if (!pageId) return 0

    if (TERMINAL_STEPS.has(pageId as EducationHostedFormSteps)) return 100

    // Unknown pageId (garbage in URL, or a new enum value missed here) — safest default.
    const stepIndex = inputSteps.indexOf(pageId as EducationHostedFormSteps)

    if (stepIndex === -1) return 0

    return (stepIndex / inputSteps.length) * 100
  }, [pageId])

  const contextValue = useMemo<EducationHostedFormContextType>(
    () => ({
      isLoading,
      formState,
      setFormState,
      progress,
      partnerConfig: partnerConfig?.data,
      otpCooldownEndsAt,
      setOtpCooldown,
      clearOtpCooldown,
      mustReverifyOtp,
      setMustReverifyOtp,
      mustReverifyCaptcha,
      setMustReverifyCaptcha,
      agentVerificationId,
      setAgentVerificationId
    }),
    [
      agentVerificationId,
      clearOtpCooldown,
      formState,
      isLoading,
      mustReverifyOtp,
      mustReverifyCaptcha,
      otpCooldownEndsAt,
      partnerConfig?.data,
      progress,
      setOtpCooldown
    ]
  )

  return <EducationHostedFormContext.Provider value={contextValue}>{children}</EducationHostedFormContext.Provider>
}
